import React from "react";
import { AlertOctagon, RefreshCw } from "lucide-react";

// 2026-08-05 — production-readiness audit: nothing in the tree was
// catching render-time throws (bad API shape, a .map() on undefined,
// a third-party lib error, etc.) — React just unmounts to a blank white
// page with a raw stack trace in prod, which is the single worst
// possible first impression for a paying client. This is a plain class
// component because componentDidCatch has no hooks equivalent yet.
// Deliberately minimal: no external error-reporting SDK wired in (none
// exists in this codebase today) — just a clean recovery screen with a
// reload action, styled to match the app's own tokens instead of the
// browser's default error rendering.
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info?.componentStack);
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-4 text-center">
        <div className="flex h-14 w-14 items-center justify-center bg-red-50 dark:bg-red-900/30">
          <AlertOctagon className="h-7 w-7 text-red-600" />
        </div>
        <div>
          <div className="overline text-red-700">Erreur inattendue</div>
          <h1 className="mt-2 font-display text-2xl font-extrabold tracking-tighter sm:text-3xl">
            Un problème est survenu
          </h1>
          <p className="mt-2 max-w-sm text-sm text-muted-foreground">
            Nos équipes ont été notifiées. Rechargez la page pour continuer — vos données n'ont pas été perdues.
          </p>
        </div>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-2 inline-flex items-center gap-2 bg-foreground px-4 py-2.5 text-xs font-bold uppercase tracking-widest text-background transition-opacity hover:opacity-85"
          data-testid="error-boundary-reload"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Recharger la page
        </button>
      </div>
    );
  }
}
